import React from 'react';
import Media from 'react-media';
import cn from 'classnames/bind';
import { Formik } from 'formik';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import CardHeader from '../../components/CardHeader/CardHeader';
import ActionButton from '../../components/ActionButton/ActionButton';
import WeighingBlock from '../../components/WeighingBlock/WeighingBlock';
import CardMilkAmounts from '../../components/CardMilkAmounts/CardMilkAmounts';
import CardInfoBlock from '../../components/CardInfoBlock/CardInfoBlock';
import CommentsBlock from '../../components/CommentsBlock/CommentsBlock';
import CardPhoto from '../../components/CardPhoto/CardPhoto';
import BindingBlock from '../../components/BindingBlock/BindingBlock';
import ActionModal from '../../components/ActionModal/ActionModal';
import { notify } from '../../../../components/Toast/Toast';

import styles from './Goat.module.css';
import { GoatsSchema } from '../../../../utils/validations';
import goat from '../../../../api/goat';
import { getCardUUID, getKeysFromUrl } from '../../../../utils/url-parser';
import { convertFormDates } from '../../../../utils/dates';
import api, { goats } from '../../../../utils/api';
import useScrollLock from '../../../../hooks/use-scroll-lock';

const cx = cn.bind(styles);

function checkCreateUrl(url) {
  const [value] = getKeysFromUrl(url, 2);

  return value === 'create';
}

// Родитель, из карточки которого нажата кнопка «Создать карточку» в поле «Потомство»
function getParentFromSearch(search) {
  const params = new URLSearchParams(search);

  if (params.get('mother')) return { field: 'motherGoat', id: params.get('mother') };
  if (params.get('father')) return { field: 'fatherGoat', id: params.get('father') };

  return null;
}

function Goat(props) {
  const url = props.location.pathname;
  const uuid = getCardUUID(url);
  const isCreate = checkCreateUrl(url);
  const createParent = isCreate ? getParentFromSearch(props.location.search) : null;
  const { targetRef, targetElement, enableBodyScroll } = useScrollLock();

  const getValues = (uuid) => {
    const requests = [goat.get(uuid), goats.goats_tree(uuid)];
    return Promise.all([
      Promise.all(requests).then((responses) => responses.map(({ data }) => data)),
      goat.getOffspring(uuid).catch(() => []),
    ])
    .then(([[goat, goatsTree], offspring]) => {
      
      const fatherObj = goatsTree.result[0].fatherObj;
      const motherObj = goatsTree.result[0].motherObj;
      setInitialValues({
        ...initialValues,
        ...convertFormDates(goat, ['birthDate','deathDate','dateMating','borkedDate','date']),
        fatherObj,
        motherObj,
        offspring,
        avatarId: goat?.avatar?.id
      })
    })
    
  }

  const [initialValues, setInitialValues] = React.useState({
    id: uuid,
    avatar: null,
    avatarId: '',
    tag: '',
    chip: '',
    name: '',
    gender: '',
    isCastrated: null,
    annotation: '',
    birthDate: null,
    dateMating: null,
    status: '',
    grownType: null,
    rejectedType: null,
    color: '',
    breed: '',
    father: '',
    fatherBreed: '',
    motherBreed: '',
    mother: '',
    // isDehydrated: null,
    isHornless: null,
    created: '',
    modified: '',
    weighing: [],
    milkAmounts: [],
    emr: '',
    isDead: null,
    deathСause: '',
    deathDate: '',
    isBorked: null,
    borkedCause: '',
    borkedDate: '',
    statusLive: null,
    fatherObj: null,
    motherObj: null,
    fatherGoat: null,
    motherGoat: null,
    isAdopted: null,
    adopter: '',
    ownerPhotos: [],
    detailedPhotos: [],
    offspring: []
  });

  React.useEffect(() => {
    if (!isCreate) {
      getValues(uuid).catch(({ response }) => {
        if (response.status) props.history.push('/goats');
      });
    }
    
  }, [isCreate, props.history, uuid]);

  // Предзаполняем «Карточка Матери» / «Карточка Отца» при создании карточки из поля «Потомство»
  const createParentField = createParent?.field;
  const createParentId = createParent?.id;

  React.useEffect(() => {
    if (!createParentId) return;

    goat.get(createParentId).then(({ data }) => {
      const parentObj = createParentField === 'motherGoat' ? 'motherObj' : 'fatherObj';

      setInitialValues((values) => ({
        ...values,
        [createParentField]: data.id,
        [parentObj]: [{ id: data.id, name: data.name, tag: data.tag }],
      }));
    });
  }, [createParentField, createParentId]);
  
  const [isEditable, setIsEditable] = React.useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  function bindingBlock(formikProps) {
    return (
      <BindingBlock
        name="emr"
        modalTitle="Выберите номер ЭМК, непривязанный ни к одной карточке козы"
        values={formikProps.values}
        url="electronic-medical-record"
        history={props.history}
        isBinded={formikProps.values.emr}
        role="goat"
        onBindClick={handleBindCard}
        onUnbindClick={handleUnbindCard}
        className={cx({ emr: true })}
        buttonClassName={cx({ 'emr-button': true })}
      />
    );
  }

  const handleSubmitForm = (handleSubmit, values) => (e) => {
    e.preventDefault();
    handleSubmit();

    const isFieldsEmpty = !!!(values.name && values.status && values.gender);

    if (isFieldsEmpty) notify('Пожалуйста, заполните необходимые поля');
  };

  function handleCreateOffspring() {
    const param = initialValues.gender === 'm' ? 'father' : 'mother';

    props.history.push(`/goat/create?${param}=${uuid}`);
  }

  // Потомство хранится у детей в mother_goat / father_goat, поэтому изменения списка
  // сохраняем, обновляя карточки добавленных и убранных коз
  function saveOffspring(values) {
    const prevOffspring = initialValues.offspring;
    const nextOffspring = values.offspring;
    const parentField = values.gender === 'm' ? 'fatherGoat' : 'motherGoat';

    const added = nextOffspring.filter(({ id }) => !prevOffspring.some((item) => item.id === id));
    const removed = prevOffspring.filter(({ id }) => !nextOffspring.some((item) => item.id === id));

    return Promise.all([
      ...added
        .filter((child) => !child[parentField])
        .map((child) => api.patch(`/goats/${child.id}/`, { [parentField]: uuid })),
      ...removed.map((child) =>
        api.patch(`/goats/${child.id}/`, {
          [child.motherGoat === uuid ? 'motherGoat' : 'fatherGoat']: null,
        }),
      ),
    ]);
  }

  function handleRemoveCard() {
    goat.remove(uuid).then(() => props.history.push('/goats'));
    enableBodyScroll(targetElement);
  }

  function onKeyDown(keyEvent) {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  }

  function handleBindCard(id, cb) {
    api.patch(`/goats/${uuid}/`, { emr: id }).then(() => {
      notify(`ЭМК успешно привязана`);
      setInitialValues({ ...initialValues, emr: id });
      cb();
    });
  }

  function handleUnbindCard(cb) {
    api.patch(`/goats/${uuid}/`, { emr: null }).then(() => {
      notify(`ЭМК успешно отвязана`);
      setInitialValues({ ...initialValues, emr: null });
      cb();
    });
  }
  
  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={GoatsSchema}
        enableReinitialize
        onSubmit={(values, actions) => {
          if (isEditable) {
            const { offspring, ...goatValues } = values;

            return goat.edit(uuid, goatValues).then(({ data }) => {
              if (values.emr) {
                api.patch(`/emr/${values.emr}/`, { goat: data.id });
              }

              return saveOffspring(values).catch(() => {
                notify('Не удалось сохранить изменения в поле «Потомство»');
              });
            }).then(() => {
              actions.setSubmitting(false);
              props.history.push('/goats');
            }).catch((error) => {
              actions.setSubmitting(false);
              if (error.response && error.response.status === 400) {
                const errorData = error.response.data;
                let errorMessage = '';
                
                // Обработка структуры ошибки {name: [{code: "unique", detail: "goat с таким name уже существует."}]}
                Object.keys(errorData).forEach(field => {
                  if (Array.isArray(errorData[field])) {
                    errorData[field].forEach(err => {
                      if (err.detail) {
                        // Заменяем техническое сообщение на более понятное
                        if (err.detail.includes('goat с таким name уже существует')) {
                          errorMessage = 'Коза с такой кличкой уже существует.';
                        } else {
                          errorMessage += err.detail + ' ';
                        }
                      }
                    });
                  }
                });
                
                notify(errorMessage.trim());
              } else {
                notify('Произошла ошибка при сохранении');
              }
            });
          }

          if (isCreate) {
            const { offspring, ...goatValues } = values;

            return goat.create(goatValues).then(() => {
              actions.setSubmitting(false);
              props.history.push(createParentId ? `/goat/${createParentId}` : '/goats');
            }).catch((error) => {
              actions.setSubmitting(false);
              if (error.response && error.response.status === 400) {
                const errorData = error.response.data;
                let errorMessage = '';
                
                // Обработка структуры ошибки {name: [{code: "unique", detail: "goat с таким name уже существует."}]}
                Object.keys(errorData).forEach(field => {
                  if (Array.isArray(errorData[field])) {
                    errorData[field].forEach(err => {
                      if (err.detail) {
                        // Заменяем техническое сообщение на более понятное
                        if (err.detail.includes('goat с таким name уже существует')) {
                          errorMessage = 'Коза с такой кличкой уже существует.';
                        } else {
                          errorMessage += err.detail + ' ';
                        }
                      }
                    });
                  }
                });
                
                notify(errorMessage.trim());
              } else {
                notify('Произошла ошибка при создании');
              }
            });
          }
        }}
        render={(formikProps) => {
          const name = formikProps.values.name;

          return (
            <div className={styles.wrapper}>
              <div className={styles.container}>
                {/* eslint-disable-next-line */}
                <form
                  noValidate
                  className={cx({ form: true, 'form-create': isCreate })}
                  onSubmit={handleSubmitForm(formikProps.handleSubmit, formikProps.values)}
                  onKeyDown={onKeyDown}
                >
                  <CardHeader
                    title={isCreate ? 'Новая карточка' : name}
                    label={
                      <Media query="(min-width: 768px) and (max-width: 1199px)">
                        {(matches) => !matches && !isCreate && bindingBlock(formikProps)}
                      </Media>
                      // !isCreate && (
                      //   <BindingBlock
                      //     name="emr"
                      //     modalTitle="Выберите номер ЭМК, непривязанный ни к одной карточке козы"
                      //     values={formikProps.values}
                      //     url="electronic-medical-record"
                      //     history={props.history}
                      //     isBinded={formikProps.values.emr}
                      //     role="goat"
                      //     onBindClick={handleBindCard}
                      //     onUnbindClick={handleUnbindCard}
                      //   />
                      // )
                    }
                    actions={
                      <>
                        {(isCreate || isEditable) && (
                          <ActionButton
                            className={cx({ 'btn-save': true })}
                            role="button"
                            icon="check-bold"
                            type="submit"
                          >
                            Сохранить
                          </ActionButton>
                        )}

                        {(isCreate || isEditable) && (
                          <ActionButton
                            className={cx({ 'btn-cancel': true })}
                            role="button"
                            icon="close-bold"
                            type="button"
                            fill="#0c1116"
                            onClick={() => setIsCloseModalOpen(true)}
                          >
                            Отменить
                          </ActionButton>
                        )}

                        {!isCreate && !isEditable && (
                          <ActionButton
                            className={cx({ 'btn-edit': true })}
                            role="button"
                            icon="pen"
                            type="button"
                            onClick={() => setIsEditable(true)}
                          >
                            Редактировать
                          </ActionButton>
                        )}

                        {!isCreate && !isEditable && (
                          <ActionButton
                            className={cx({ 'btn-remove': true })}
                            role="button"
                            icon="trash"
                            type="button"
                            onClick={() => setIsDeleteModalOpen(true)}
                          >
                            Удалить
                          </ActionButton>
                        )}
                      </>
                    }
                  />

                  <CardPhoto
                    emr={
                      <Media query="(min-width: 768px) and (max-width: 1199px)">
                        {(matches) => matches && bindingBlock(formikProps)}
                      </Media>
                    }
                    isEditable={isEditable || isCreate}
                    isCreate={isCreate}
                    {...formikProps}
                  />

                  <Media query="(max-width: 767px)">
                    {(matches) =>
                      matches ? (
                        <Tabs className={styles.tabs}>
                          <TabList>
                            <Tab>Информация</Tab>
                            <Tab>Удой</Tab>
                            <Tab>Взвешивания</Tab>
                          </TabList>

                          <TabPanel>
                            <CardInfoBlock
                              isEditable={isEditable || isCreate}
                              isCreate={isCreate}
                              onCreateOffspring={handleCreateOffspring}
                              {...formikProps}
                            />
                          </TabPanel>

                          <TabPanel>
                            <CardMilkAmounts isEditable={isEditable || isCreate} {...formikProps} />
                          </TabPanel>

                          <TabPanel>
                            <WeighingBlock isEditable={isEditable || isCreate} {...formikProps} />
                          </TabPanel>
                        </Tabs>
                      ) : (
                        <>
                          <div className={cx({ 'blocks-wrapper': true })}>
                            <div>
                              <CardMilkAmounts
                                isEditable={isEditable || isCreate}
                                {...formikProps}
                              />
                            </div>

                            <div>
                              <WeighingBlock isEditable={isEditable || isCreate} {...formikProps} />
                            </div>
                          </div>

                          <CardInfoBlock
                            isEditable={isEditable || isCreate}
                            isCreate={isCreate}
                            onCreateOffspring={handleCreateOffspring}
                            {...formikProps}
                          />
                        </>
                      )
                    }
                  </Media>
                </form>
              </div>

              {!isCreate && <CommentsBlock uuid={uuid} page="/goats" />}
            </div>
          );
        }}
      />

      <ActionModal
        isOpen={isCloseModalOpen}
        title="Закрыть документ?"
        btnAcceptText="Закрыть"
        btnDeclineText="Продолжить редактирование"
        onClose={() => {
          setIsCloseModalOpen(false);
          enableBodyScroll(targetElement);
        }}
        onAccept={() => {
          props.history.push(createParentId ? `/goat/${createParentId}` : '/goats');
          enableBodyScroll(targetElement);
        }}
        ref={targetRef}
        target={targetElement}
      >
        Если вы закроете документ, внесенные изменения не сохранятся
      </ActionModal>

      <ActionModal
        isOpen={isDeleteModalOpen}
        title="Вы действительно хотите удалить выбранную карточку?"
        btnAcceptText="Удалить"
        btnDeclineText="Отмена"
        onClose={() => {
          setIsDeleteModalOpen(false);
          enableBodyScroll(targetElement);
        }}
        onAccept={handleRemoveCard}
        ref={targetRef}
        target={targetElement}
      />
    </>
  );
}

export default Goat;
